import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/KeycloakContext';
import './AuditLogs.css';
import './AdminDashboard.css';
import '../workspace/Members.css';
import Pagination from '../../ui/Pagination';
import Toast from '../../ui/Toast';
import Banner from '../../ui/Banner';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AuditLogs = ({ activeTab, setActiveTab }) => {
  const { keycloak } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    action: '',
    startDate: '',
    endDate: '',
    success: '',
  });
  const [sort, setSort] = useState({ sortBy: 'timestamp', sortOrder: 'DESC' });
  const [pageInput, setPageInput] = useState('1');

  useEffect(() => {
    if (keycloak && keycloak.authenticated) {
      fetchLogs();
    }
  }, [pagination.page, pagination.limit, filters, sort, keycloak]);

  useEffect(() => {
    setPageInput(String(pagination.page));
  }, [pagination.page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setToast({ ...toast, show: false });

      if (!keycloak || !keycloak.authenticated || !keycloak.token) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== '')),
      });

      const response = await fetch(`${API_URL}/admin/audit-logs?${params}`, {
        headers: {
          Authorization: `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch audit logs: ${response.statusText}`);
      }

      const data = await response.json();
      setLogs(data.logs || []);
      setPagination((prev) => ({
        ...prev,
        total: data.pagination?.total || 0,
        totalPages: data.pagination?.totalPages || 0,
      }));
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setToast({ show: true, message: err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page on filter change
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleSort = (field) => {
    const isAsc = sort.sortBy === field && sort.sortOrder === 'ASC';
    setSort({
      sortBy: field,
      sortOrder: isAsc ? 'DESC' : 'ASC',
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const getSortIcon = (field) => {
    if (sort.sortBy !== field)
      return <i className="fas fa-sort" style={{ marginLeft: '8px', opacity: 0.3 }}></i>;
    return sort.sortOrder === 'ASC' ? (
      <i className="fas fa-sort-up" style={{ marginLeft: '8px', color: '#3b82f6' }}></i>
    ) : (
      <i className="fas fa-sort-down" style={{ marginLeft: '8px', color: '#3b82f6' }}></i>
    );
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getActionBadgeClass = (action) => {
    if (action.includes('CREATE')) return 'badge-success';
    if (action.includes('UPDATE') || action.includes('ASSIGN')) return 'badge-info';
    if (action.includes('DELETE') || action.includes('REMOVE')) return 'badge-danger';
    return 'badge-default';
  };

  const columns = [
    { key: 'admin_email', label: 'Admin', sortable: true, width: '25%' },
    { key: 'action', label: 'Action', sortable: true, width: '15%' },
    { key: 'target_name', label: 'Target', sortable: true, width: '20%' },
    { key: 'ip_address', label: 'IP Address', sortable: true, width: '15%' },
    { key: 'timestamp', label: 'Timestamp', sortable: true, width: '15%' },
    { key: 'success', label: 'Status', sortable: true, width: '10%' },
  ];

  const renderLogRow = (log) => (
    <tr key={log.id}>
      <td className="col-member">
        <div className="tm-member-cell">
          <div className="tm-avatar" style={{ background: '#475569' }}>
            {(log.admin_username || log.admin_email || '??').substring(0, 2).toUpperCase()}
          </div>
          <div className="tm-member-info" style={{ overflow: 'hidden' }}>
            <span
              className="tm-name"
              style={{
                textTransform: 'none',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
            >
              {log.admin_username || log.admin_email || 'Unknown'}
            </span>
            <span
              className="tm-email"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
            >
              {log.admin_email}
            </span>
          </div>
        </div>
      </td>
      <td style={{ overflow: 'hidden' }}>
        <span
          className={`tm-role-pill ${getActionBadgeClass(log.action)}`}
          style={{
            background: '#f1f5f9',
            color: '#475569',
            border: '1px solid #e2e8f0',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'inline-block',
            maxWidth: '100%',
          }}
        >
          {log.action.replace(/_/g, ' ')}
        </span>
      </td>
      <td style={{ overflow: 'hidden' }}>
        <div className="target-cell" style={{ overflow: 'hidden' }}>
          <div
            className="target-name"
            style={{
              fontWeight: 500,
              color: '#0f172a',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {log.target_name || log.target_id || '-'}
          </div>
          {log.target_type && (
            <div className="target-type" style={{ fontSize: '11px', color: '#64748b' }}>
              {log.target_type}
            </div>
          )}
        </div>
      </td>
      <td style={{ color: '#64748b' }}>{log.ip_address || '-'}</td>
      <td style={{ color: '#64748b' }}>{formatTimestamp(log.timestamp)}</td>
      <td className="col-credit">
        <span className={`tm-credit-badge tm-credit-${log.success ? 'active' : 'inactive'}`}>
          {log.success ? 'Success' : 'Failed'}
        </span>
      </td>
    </tr>
  );

  return (
    <div
      className="tm-page"
      style={{ padding: '0 0 40px 0', minHeight: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Banner
        title="User Management"
        subtitle="Manage user accounts, roles, and access across your platform."
        type="page-header"
      />

      <div className="tm-section-tabs">
        <button
          className={`tm-section-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <i className="fas fa-users"></i> All Users
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${activeTab === 'audit-logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('audit-logs')}
        >
          <i className="fas fa-history"></i> Audit Logs
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${activeTab === 'platform-activity' ? 'active' : ''}`}
          onClick={() => setActiveTab('platform-activity')}
        >
          <i className="fas fa-chart-line"></i> Platform Activity
        </button>
      </div>
      <div className="tm-horizontal-divider"></div>

      <div className="animate-fade-in" style={{ padding: '12px 0' }}>
        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, show: false })}
          />
        )}

        <div className="tm-toolbar">
          <div className="tm-toolbar-group">
            <label className="tm-toolbar-label">Action</label>
            <select
              className="tm-toolbar-select"
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
            >
              <option value="">All actions</option>
              <option value="CREATE_PROSPECT">Search</option>
              <option value="CREATE_PROSPECT_ENRICHMENT">Enrichment</option>
              <option value="INVITE_USER">Invite User</option>
              <option value="JOIN_WORKSPACE">Join Workspace</option>
              <option value="CHANGE_ROLE">Change Role</option>
              <option value="MARKET_ANALYSIS_SEARCH">Market Analysis</option>
              <option value="B2C_RECOMMENDATION_START">Search Persona</option>
              <option value="REMOVE_USER">Remove Member</option>
              <option value="CREATE_WORKSPACE">Create Workspace</option>
              <option value="DELETE_USER">Delete User</option>
              <option value="CREATE_USER">Create User</option>
            </select>
          </div>
          <div className="tm-toolbar-group">
            <label className="tm-toolbar-label">Status</label>
            <select
              className="tm-toolbar-select"
              value={filters.success}
              onChange={(e) => handleFilterChange('success', e.target.value)}
            >
              <option value="">All status</option>
              <option value="true">Success</option>
              <option value="false">Failed</option>
            </select>
          </div>
          <div className="tm-toolbar-group">
            <label className="tm-toolbar-label">From</label>
            <input
              className="tm-toolbar-input"
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>
          <div className="tm-toolbar-group">
            <label className="tm-toolbar-label">To</label>
            <input
              className="tm-toolbar-input"
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>
          <div
            className="tm-toolbar-group tm-toolbar-actions"
            style={{ minWidth: '118px', flex: '0 0 118px' }}
          >
            <label className="tm-toolbar-label" style={{ opacity: 0 }}>
              &nbsp;
            </label>
            <button
              className="tm-btn tm-btn-outline"
              style={{ width: '100%', justifyContent: 'center', height: '32px' }}
              onClick={() => {
                setFilters({
                  action: '',
                  startDate: '',
                  endDate: '',
                  success: '',
                });
                setSort({ sortBy: 'timestamp', sortOrder: 'DESC' });
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
            >
              <i className="fas fa-times"></i> Clear
            </button>
          </div>
        </div>

        <Pagination
          columns={columns}
          data={logs}
          renderRow={renderLogRow}
          loading={loading}
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={pagination.limit}
          onItemsPerPageChange={(limit) => setPagination((prev) => ({ ...prev, page: 1, limit }))}
          totalItems={pagination.total}
          itemsName="records"
          sortBy={sort.sortBy}
          sortOrder={sort.sortOrder}
          onSort={handleSort}
          emptyState={
            <div className="tm-empty">
              <i className="fas fa-history"></i>
              <p>No audit logs found.</p>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default AuditLogs;
